import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SubscriptionService from "../../../../services/subscription-service";
import useModuleInfo from "../../../../hooks/useModuleInfo";
import useFetch, { useCacheActions } from "../../../../hooks/useFetch";
import useShortCode from "../../../../hooks/useShortCode";
import useSubscribe from "../../../../hooks/useSubscribe";
import usePush from "../../../../hooks/usePush";
import { addonIdToFeature } from "../../../../../src/util/constants";
import { addonIds, BillingPeriods, Modules } from "../../../../util/constants";
import AddonDowngradeModal from "../../../../components/Addon/DowngradeModal";
import useModal from "../../../../hooks/useModal";
import useOrganisationUtils from "../../../../hooks/useOrganisationUtils";
import GeneralService from "../../../../services/general-service";
import {
  ADDITIONAL_FEATURE_ADDON_IDS,
  ADDON_LABELS,
  CREDIT_ADDON_IDS,
  getIncludedDefaultLabel,
  getPlanMaxRevenueText,
  getUsageUnitPriceMinor,
  ICON_MAP,
  mapIncludedInPlanToPlanLimit,
  toAmountMinorForUsage,
  toMinor,
} from "./utils";
import { BASE_MODULE_ID, ORGANISATION_MODULE_ID } from "./constants";
import { getFeatureLimitDefaults, normalizeOfferPeriod } from "./planHelpers";
import {
  getAnnualizedTotalMinorFromSummary,
  getDefaultModuleId,
  getMetadataFeatureFlags,
  getSummaryTotalsForKeywords,
  getTrialPlanNameFromModuleId,
  hasSharedLimitOverages,
  isExpiredTrialModule,
  isOrganisationModuleMissingAfterAddonsLoad,
  isTrialPlanName,
  normalizeCompareToken,
  resolveFreeUsers,
  sumAdditionalUsersAmountMinorFromSummary,
  sumAdditionalUsersQuantityFromSummary,
  toDisplayLabel,
} from "./useAddonsState.helpers";

const subscribeDispatch = () => {};
const noopPlans = async () => null;
const noopAddons = async () => [];

const getPlansForModule = async (moduleName) =>
  GeneralService.getAllPlans({ moduleName });

const SUMMARY_KEYWORDS_BY_ADDON_ID = {
  [addonIds.TEMPLATE]: ["template", "templates"],
  [addonIds.SCHEDULE]: ["schedule", "schedules"],
  [addonIds.EMAIL_INTEGRATION]: [
    "email integration",
    "email integrations",
    "direct email integration",
  ],
  [addonIds.USER]: ["user", "users"],
};

const UNLIMITED_USERS_SENTINEL = 9999;

const useAddonsState = (initialSelectedModuleId = null) => {
  const showModal = useModal();
  const { formatCurrency } = useOrganisationUtils();

  const shortCode = useShortCode();
  const [activeAddonId, setActiveAddonId] = useState(null);
  const [removingAddonId, setRemovingAddonId] = useState(null);
  const [showComparePlans, setShowComparePlans] = useState(false);
  const [targetPlanName, setTargetPlanName] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(
    initialSelectedModuleId || null,
  );
  const [offerPeriodByModule, setOfferPeriodByModule] = useState({});
  const [isStartingModuleTrial, setIsStartingModuleTrial] = useState(false);
  const isModulePlanActionInFlight = useRef(false);
  const addonsCacheByModuleRef = useRef({});
  const plansCacheByModuleRef = useRef({});

  const shouldFetchAvailableAddons = Boolean(selectedModuleId);

  const {
    data: availableAddons,
    loading: loadingAddons,
    refetch: refetchSelectedModuleAddons,
  } = useFetch(
    shouldFetchAvailableAddons
      ? SubscriptionService.getAvailableAddons
      : noopAddons,
    shouldFetchAvailableAddons
      ? {
          shortCode,
          moduleName: selectedModuleId,
        }
      : null,
    {
      suspense: false,
    },
  );

  const {
    data: organisationAvailableAddons,
    refetch: refetchOrganisationAddons,
  } = useFetch(
    shortCode ? SubscriptionService.getAvailableAddons : noopAddons,
    shortCode
      ? {
          shortCode,
          moduleName: ORGANISATION_MODULE_ID,
        }
      : null,
    {
      suspense: false,
    },
  );

  useEffect(() => {
    if (initialSelectedModuleId) {
      setSelectedModuleId(initialSelectedModuleId);
    }
  }, [initialSelectedModuleId]);

  const {
    module: moduleData,
    plan,
    plans,
    currencyCode,
    refetchSubscriptionData,
    loading: loadingSubscription,
  } = useModuleInfo(undefined, { suspense: false });

  const shouldFetchSelectedModulePlans =
    Boolean(selectedModuleId) && selectedModuleId !== BASE_MODULE_ID;
  const { data: modulePlansById, loading: loadingModulePlans } = useFetch(
    shouldFetchSelectedModulePlans ? getPlansForModule : noopPlans,
    shouldFetchSelectedModulePlans ? selectedModuleId : null,
    {
      suspense: false,
    },
  );

  useEffect(() => {
    if (!selectedModuleId) return;
    if (!Array.isArray(availableAddons)) return;

    addonsCacheByModuleRef.current[selectedModuleId] = availableAddons;
  }, [availableAddons, selectedModuleId]);

  useEffect(() => {
    if (!shouldFetchSelectedModulePlans || !selectedModuleId) return;
    if (!modulePlansById) return;

    plansCacheByModuleRef.current[selectedModuleId] = modulePlansById;
  }, [modulePlansById, selectedModuleId, shouldFetchSelectedModulePlans]);

  const cachedAvailableAddons = selectedModuleId
    ? addonsCacheByModuleRef.current[selectedModuleId]
    : null;
  const resolvedAvailableAddons = useMemo(() => {
    if (Array.isArray(availableAddons)) {
      return availableAddons;
    }

    if (Array.isArray(cachedAvailableAddons)) {
      return cachedAvailableAddons;
    }

    return [];
  }, [availableAddons, cachedAvailableAddons]);

  const cachedModulePlansById =
    shouldFetchSelectedModulePlans && selectedModuleId
      ? plansCacheByModuleRef.current[selectedModuleId]
      : null;
  const resolvedModulePlansById =
    modulePlansById || cachedModulePlansById || null;

  const moduleSubscriptions = useMemo(() => {
    return moduleData?.moduleSubscriptions || [];
  }, [moduleData?.moduleSubscriptions]);

  const { invalidateDomainAndWait } = useCacheActions();
  const [isRefreshingOrganisationModule, setIsRefreshingOrganisationModule] =
    useState(false);
  const hasRefetchedForOrganisationModuleRef = useRef(false);
  useEffect(() => {
    if (hasRefetchedForOrganisationModuleRef.current) return;
    if (
      !isOrganisationModuleMissingAfterAddonsLoad({
        organisationAvailableAddons,
        moduleSubscriptions,
        loadingSubscription,
      })
    ) {
      return;
    }

    hasRefetchedForOrganisationModuleRef.current = true;
    setIsRefreshingOrganisationModule(true);
    invalidateDomainAndWait("organisation")
      .catch(() => {})
      .finally(() => setIsRefreshingOrganisationModule(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationAvailableAddons, moduleSubscriptions, loadingSubscription]);

  const isSettingUpOrganisationModule =
    selectedModuleId === ORGANISATION_MODULE_ID &&
    (isRefreshingOrganisationModule ||
      (!Array.isArray(organisationAvailableAddons) &&
        !moduleSubscriptions.some(
          (module) => module?.id === ORGANISATION_MODULE_ID,
        )));

  const activeModuleSubscriptions = useMemo(
    () =>
      moduleSubscriptions.filter(
        (module) =>
          Boolean(module?.isActive) ||
          Boolean(module?.isInTrial) ||
          Boolean(isExpiredTrialModule(module)),
      ),
    [moduleSubscriptions],
  );

  const availableModuleOptions = useMemo(
    () =>
      moduleSubscriptions.filter(
        (module) => !module?.isActive && !module?.isInTrial,
      ),
    [moduleSubscriptions],
  );

  useEffect(() => {
    const selectedExists =
      activeModuleSubscriptions.some(
        (module) => module.id === selectedModuleId,
      ) ||
      availableModuleOptions.some((module) => module.id === selectedModuleId) ||
      selectedModuleId === ORGANISATION_MODULE_ID;

    if (selectedExists) return;

    const defaultModuleId = getDefaultModuleId(
      activeModuleSubscriptions,
      availableModuleOptions,
    );

    if (defaultModuleId) {
      setSelectedModuleId(defaultModuleId);
    }
  }, [activeModuleSubscriptions, availableModuleOptions, selectedModuleId]);

  const selectedModule =
    moduleSubscriptions.find((module) => module.id === selectedModuleId) ||
    null;

  const selectedOfferPeriod = useMemo(() => {
    if (!selectedModuleId) return BillingPeriods.MONTHLY;

    const storedPeriod = offerPeriodByModule[selectedModuleId];
    if (storedPeriod) return normalizeOfferPeriod(storedPeriod);

    const selectedFromList = moduleSubscriptions.find(
      (module) => module.id === selectedModuleId,
    );

    return normalizeOfferPeriod(
      selectedFromList?.period || selectedFromList?.plan?.period,
    );
  }, [selectedModuleId, offerPeriodByModule, moduleSubscriptions]);

  const setSelectedOfferPeriod = (nextPeriod) => {
    if (!selectedModuleId) return;

    setOfferPeriodByModule((prev) => ({
      ...prev,
      [selectedModuleId]: normalizeOfferPeriod(nextPeriod),
    }));
  };

  const selectedPlan = selectedModule?.plan;
  const selectedModuleSupportsAddons = Boolean(selectedModule?.supportsAddons);
  const isSelectedModuleOfferFlow = Boolean(selectedModule?.isInTrial);
  const selectedPlanPeriod = isSelectedModuleOfferFlow
    ? selectedOfferPeriod
    : selectedModule?.period ||
      selectedPlan?.period ||
      plan?.period ||
      BillingPeriods.MONTHLY;
  const checkoutPeriod =
    normalizeOfferPeriod(selectedOfferPeriod) === BillingPeriods.YEARLY
      ? BillingPeriods.YEARLY
      : BillingPeriods.MONTHLY;
  const comparisonPeriod =
    selectedOfferPeriod === BillingPeriods.YEARLY
      ? BillingPeriods.YEARLY
      : BillingPeriods.MONTHLY;

  const upgradePlan = useSubscribe(
    subscribeDispatch,
    {
      planName: targetPlanName,
      currencyCode,
      period: checkoutPeriod,
      moduleName: selectedModule?.id,
    },
    async () => {
      await refetchSubscriptionData();
      setShowComparePlans(false);
    },
  );

  const { execute: startModuleSubscriptionTrial } = usePush(
    SubscriptionService.startModuleSubscriptionTrial,
    {
      errorToast: "Failed to start module trial. Please try again.",
    },
  );

  useEffect(() => {
    if (!targetPlanName) return;
    if (isModulePlanActionInFlight.current) return;

    const targetModuleId = selectedModule?.id;
    if (!targetModuleId) {
      setTargetPlanName(null);
      return;
    }

    if (
      !selectedModule?.isActive &&
      !selectedModule?.isInTrial &&
      !isExpiredTrialModule(selectedModule)
    ) {
      setTargetPlanName(null);
      return;
    }

    isModulePlanActionInFlight.current = true;

    const run = async () => {
      try {
        await upgradePlan();
      } finally {
        isModulePlanActionInFlight.current = false;
        setTargetPlanName(null);
      }
    };

    run();
  }, [
    targetPlanName,
    selectedModule,
    selectedModule?.id,
    selectedModule?.isActive,
    selectedModule?.isInTrial,
    shortCode,
    currencyCode,
    selectedOfferPeriod,
    startModuleSubscriptionTrial,
    refetchSubscriptionData,
    upgradePlan,
  ]);

  const startSelectedModuleTrial = async () => {
    if (isModulePlanActionInFlight.current) return;

    const targetModuleId = selectedModule?.id;
    if (
      !targetModuleId ||
      selectedModule?.isActive ||
      selectedModule?.isInTrial
    ) {
      return;
    }

    const trialPlanName = getTrialPlanNameFromModuleId(targetModuleId);
    if (!trialPlanName) {
      return;
    }

    isModulePlanActionInFlight.current = true;
    setIsStartingModuleTrial(true);

    try {
      const [trialError] = await startModuleSubscriptionTrial({
        shortCode,
        moduleName: targetModuleId,
        planName: trialPlanName,
        currency: currencyCode,
        period: BillingPeriods.MONTHLY,
      });

      if (trialError) {
        return;
      }

      await refetchSubscriptionData();
      setShowComparePlans(false);
    } finally {
      isModulePlanActionInFlight.current = false;
      setIsStartingModuleTrial(false);
    }
  };

  const loadingAddonsForUi =
    loadingAddons &&
    !Array.isArray(availableAddons) &&
    !Array.isArray(cachedAvailableAddons);

  const loadingModulePlansForUi =
    shouldFetchSelectedModulePlans &&
    loadingModulePlans &&
    !modulePlansById &&
    !cachedModulePlansById;

  const loading =
    loadingAddonsForUi || loadingSubscription || loadingModulePlansForUi;

  const hasHydratedModuleStateRef = useRef(false);

  useEffect(() => {
    if (!loadingSubscription && moduleSubscriptions.length > 0) {
      hasHydratedModuleStateRef.current = true;
    }
  }, [loadingSubscription, moduleSubscriptions.length]);

  const isInitialLoading =
    !hasHydratedModuleStateRef.current && Boolean(loading);

  const isModuleSwitching =
    hasHydratedModuleStateRef.current &&
    (loadingAddonsForUi ||
      loadingModulePlansForUi ||
      isSettingUpOrganisationModule);

  const selectedModuleAddons = useMemo(() => {
    if (!selectedModuleSupportsAddons) return {};

    return selectedModule?.addons || selectedPlan?.addons || {};
  }, [
    selectedModule?.addons,
    selectedModuleSupportsAddons,
    selectedPlan?.addons,
  ]);

  const currentAddons = selectedModuleAddons;

  const addonsById = useMemo(
    () =>
      resolvedAvailableAddons.reduce((acc, addon) => {
        acc[addon.id] = addon;
        return acc;
      }, {}),
    [resolvedAvailableAddons],
  );

  const summaryTotalsByAddonId = useMemo(() => {
    const lineItems = moduleData?.summary?.lineItems;

    return Object.entries(SUMMARY_KEYWORDS_BY_ADDON_ID).reduce(
      (acc, [addonId, keywords]) => {
        acc[addonId] = getSummaryTotalsForKeywords(lineItems, keywords);
        return acc;
      },
      {},
    );
  }, [moduleData?.summary?.lineItems]);

  const resolveUsageByAddonId = (addonId, addonUsageMap) => {
    const featureKey = addonIdToFeature[addonId];
    if (featureKey) return addonUsageMap?.[featureKey];

    if (String(addonId || "").includes("user")) {
      return addonUsageMap?.users;
    }

    return null;
  };

  const buildRow = useCallback(
    (addonId) => {
      const addon = addonsById[addonId];
      if (!addon) return null;

      const usage = resolveUsageByAddonId(addonId, currentAddons);
      const summaryTotals = summaryTotalsByAddonId[addonId] || {
        quantity: 0,
        amountMinor: 0,
      };
      const purchasedQuantity = Math.max(
        toMinor(usage?.overages),
        toMinor(addon?.quantity),
        toMinor(summaryTotals.quantity),
      );
      const usageWithQuantity = {
        ...(usage || {}),
        overages: purchasedQuantity,
        proratedOverages: toMinor(usage?.proratedOverages ?? purchasedQuantity),
      };

      const sharedLimitsUsed = Math.max(
        0,
        ...Object.values(usageWithQuantity?.sharedLimits || {}).map((limit) =>
          toMinor(limit?.totalUsed),
        ),
      );

      const used = toMinor(
        usageWithQuantity?.totalUsed ??
          (sharedLimitsUsed > 0
            ? sharedLimitsUsed
            : usageWithQuantity?.overages),
      );
      const includedLimit = toMinor(usageWithQuantity?.planLimit);
      const totalVolumeCredits = toMinor(
        usageWithQuantity?.proratedOverages + usageWithQuantity?.planLimit,
      );

      const limit =
        addon.type === "FLAT_FEE"
          ? includedLimit
          : addon.type === "VOLUME"
            ? totalVolumeCredits
            : toMinor(includedLimit + purchasedQuantity);

      const percentage =
        limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;

      const hasSharedOverage = hasSharedLimitOverages(
        usageWithQuantity?.sharedLimits,
      );
      const isOverLimit =
        Number(usageWithQuantity?.overLimits || 0) > 0 ||
        hasSharedOverage ||
        (Number.isFinite(limit) && limit > 0 && used > limit);

      const activeFromUsage =
        addon.type === "FLAT_FEE"
          ? Boolean(
              toMinor(usageWithQuantity?.overages) > 0 ||
              usageWithQuantity?.used,
            )
          : purchasedQuantity > 0;

      const isIncluded = Boolean(usageWithQuantity?.includedInPlan);
      const isActive = activeFromUsage || isIncluded;
      const rowAmountMinor = Math.max(
        toAmountMinorForUsage(addon, usageWithQuantity),
        toMinor(summaryTotals.amountMinor),
      );

      return {
        addon,
        addonId,
        moduleName: selectedModuleId || BASE_MODULE_ID,
        icon: ICON_MAP[addonId] || "plus-circle",
        name: ADDON_LABELS[addonId] || addon.name,
        usage: usageWithQuantity,
        used,
        limit,
        includedLimit,
        purchasedQuantity,
        percentage,
        isOverLimit,
        rowAmountMinor,
        isIncluded,
        isActive,
        isFlatFee: addon.type === "FLAT_FEE",
      };
    },
    [addonsById, currentAddons, selectedModuleId, summaryTotalsByAddonId],
  );

  const credits = useMemo(
    () => CREDIT_ADDON_IDS.map(buildRow).filter(Boolean),
    [buildRow],
  );

  const additionalFeatures = useMemo(
    () => ADDITIONAL_FEATURE_ADDON_IDS.map(buildRow).filter(Boolean),
    [buildRow],
  );

  const organisationUsersIncludedLimit = useMemo(() => {
    const planEntries = Object.values(resolvedModulePlansById || {}).filter(
      Boolean,
    );
    if (!planEntries.length) return 0;

    const normalizedSelectedPlanName = normalizeCompareToken(
      selectedModule?.planName,
    );

    const matchedPlan = planEntries.find((plan) => {
      const nameMatches =
        normalizeCompareToken(plan?.name) === normalizedSelectedPlanName;
      const titleMatches =
        normalizeCompareToken(plan?.title) === normalizedSelectedPlanName;

      return nameMatches || titleMatches;
    });

    const usersOnMatchedPlan = Number(matchedPlan?.features?.users);
    if (Number.isFinite(usersOnMatchedPlan) && usersOnMatchedPlan >= 0) {
      return toMinor(usersOnMatchedPlan);
    }

    const firstPlanWithUsers = planEntries.find((plan) => {
      const users = Number(plan?.features?.users);
      return Number.isFinite(users) && users >= 0;
    });

    return toMinor(firstPlanWithUsers?.features?.users);
  }, [resolvedModulePlansById, selectedModule?.planName]);

  const hasTrialUnlimitedUsers = useMemo(() => {
    return (moduleSubscriptions || []).some((module) => {
      if (!module?.isInTrial) return false;

      const planUsers = Number(module?.plan?.features?.users);
      if (Number.isFinite(planUsers) && planUsers >= UNLIMITED_USERS_SENTINEL) {
        return true;
      }

      return false;
    });
  }, [moduleSubscriptions]);

  const totalAdditionalUsersQtyFromSummary = useMemo(() => {
    return sumAdditionalUsersQuantityFromSummary(
      moduleData?.summary?.lineItems,
    );
  }, [moduleData?.summary?.lineItems]);

  const totalAdditionalUsersAmountMinorFromSummary = useMemo(() => {
    return sumAdditionalUsersAmountMinorFromSummary(
      moduleData?.summary?.lineItems,
    );
  }, [moduleData?.summary?.lineItems]);

  const organisationAddonRows = useMemo(() => {
    const usersAddon =
      (organisationAvailableAddons || []).find(
        (addon) => addon?.id === addonIds.ADDITIONAL_USERS,
      ) ||
      (organisationAvailableAddons || []).find(
        (addon) => addon?.chargebeeAddonId === addonIds.ADDITIONAL_USERS,
      ) ||
      (organisationAvailableAddons || []).find(
        (addon) => addonIdToFeature[addon?.id] === "users",
      );

    const addonId = usersAddon?.id;
    const addon = usersAddon || null;
    if (!addon) return [];

    const usageFromPlan = resolveUsageByAddonId(addonId, selectedModuleAddons);
    const includedUsersByPlan = Array.isArray(
      usageFromPlan?.includedUsersByPlan,
    )
      ? usageFromPlan.includedUsersByPlan
      : [];
    const hasUnlimitedIncludedUsersByPlan = includedUsersByPlan.some(
      (planEntry) =>
        toMinor(planEntry?.includedUsers) >= UNLIMITED_USERS_SENTINEL,
    );
    const includedUsersFromModulePlan = toMinor(addon?.includedQuantity);
    const totalIncludedUsersFromApi = toMinor(
      usageFromPlan?.totalIncludedUsers,
    );
    const baseUsersLimit = hasTrialUnlimitedUsers
      ? UNLIMITED_USERS_SENTINEL
      : toMinor(
          totalIncludedUsersFromApi ||
            includedUsersFromModulePlan ||
            organisationUsersIncludedLimit ||
            usageFromPlan?.planLimit,
        );
    const additionalUsersQty = Math.max(
      toMinor(usageFromPlan?.totalPurchasedUsers),
      toMinor(addon?.quantity),
      toMinor(usageFromPlan?.overages),
      toMinor(usageFromPlan?.proratedOverages),
      toMinor(totalAdditionalUsersQtyFromSummary),
    );

    const totalUsed = toMinor(
      usageFromPlan?.totalUsed ?? usageFromPlan?.overages,
    );
    const isUnlimitedUsersLimit =
      hasTrialUnlimitedUsers ||
      Boolean(usageFromPlan?.isUnlimitedUsersLimit) ||
      hasUnlimitedIncludedUsersByPlan ||
      toMinor(usageFromPlan?.totalUsersLimit) >= UNLIMITED_USERS_SENTINEL ||
      baseUsersLimit >= UNLIMITED_USERS_SENTINEL;
    const limit = isUnlimitedUsersLimit
      ? UNLIMITED_USERS_SENTINEL
      : toMinor(usageFromPlan?.totalUsersLimit) ||
        baseUsersLimit + additionalUsersQty;
    const percentage =
      !isUnlimitedUsersLimit && limit > 0
        ? Math.min(100, Math.round((totalUsed / limit) * 100))
        : 0;

    const isOverLimit =
      Number(usageFromPlan?.overLimits || 0) > 0 ||
      (Number.isFinite(limit) && limit > 0 && totalUsed > limit);

    const usage = {
      ...(usageFromPlan || {}),
      totalUsed,
      overages: additionalUsersQty,
      proratedOverages: additionalUsersQty,
      planLimit: limit,
      totalIncludedUsers: baseUsersLimit,
      totalPurchasedUsers: additionalUsersQty,
      totalUsersLimit: limit,
      isUnlimitedUsersLimit,
      includedUsersByPlan,
    };

    const usersAmountMinor =
      additionalUsersQty * getUsageUnitPriceMinor(addon, usageFromPlan);

    return [
      {
        id: addonId,
        addon,
        moduleName: ORGANISATION_MODULE_ID,
        addonId: addonIds.ADDITIONAL_USERS,
        name:
          ADDON_LABELS[addonIds.ADDITIONAL_USERS] ||
          ADDON_LABELS[addonId] ||
          addon.name ||
          "Users add-on",
        usage,
        limit,
        percentage,
        isOverLimit,
        freeAllowance: baseUsersLimit,
        purchasedAllowance: additionalUsersQty,
        totalUsersLimit: limit,
        isUnlimitedUsersLimit,
        includedUsersByPlan,
        rowAmountMinor: Math.max(
          usersAmountMinor,
          totalAdditionalUsersAmountMinorFromSummary,
        ),
        isIncluded: Boolean(usage?.includedInPlan),
        isActive: true,
      },
    ];
  }, [
    organisationAvailableAddons,
    selectedModuleAddons,
    organisationUsersIncludedLimit,
    hasTrialUnlimitedUsers,
    totalAdditionalUsersQtyFromSummary,
    totalAdditionalUsersAmountMinorFromSummary,
  ]);

  const planAmountMinor = toMinor(plan?.amount);
  const selectedModuleAmountMinor = toMinor(selectedModule?.amountMinor);
  const moduleTotalCurrentCadenceMinor =
    toMinor(moduleData?.summary?.totalAmountMinor) ||
    moduleSubscriptions.reduce(
      (sum, module) => sum + toMinor(module.amountMinor),
      0,
    );
  const moduleTotalAnnualEquivalentMinor = useMemo(() => {
    return getAnnualizedTotalMinorFromSummary({
      summary: moduleData?.summary,
      moduleSubscriptions,
    });
  }, [moduleData?.summary, moduleSubscriptions]);
  const currentPlanAmount = planAmountMinor / 100;

  const onActionClick = (row) => {
    if (row.isIncluded && row.isFlatFee) {
      return;
    }

    if (row.isActive && row.isFlatFee && !row.isIncluded) {
      showModal(
        <AddonDowngradeModal addon={row.addon} moduleName={row.moduleName} />,
      );
      return;
    }

    setActiveAddonId(row.addonId);
  };

  const onRemoveClick = (row) => {
    if (!row?.addon || !row?.isActive) {
      return;
    }

    const purchasedQuantity = Number(
      row.purchasedQuantity ??
        row.purchasedAllowance ??
        row?.usage?.overages ??
        row?.usage?.proratedOverages ??
        0,
    );
    const purchasedAmountMinor = Number(row?.rowAmountMinor || 0);

    if (purchasedQuantity <= 0 && purchasedAmountMinor <= 0) {
      return;
    }

    showModal(
      <AddonDowngradeModal
        addon={row.addon}
        moduleName={row.moduleName}
        onSuccess={(addonId) => {
          const normalizedAddonId =
            addonId === addonIds.USER || addonId === addonIds.ADDITIONAL_USERS
              ? addonIds.ADDITIONAL_USERS
              : addonId;
          setRemovingAddonId(normalizedAddonId);
          refetchOrganisationAddons();
        }}
      />,
    );
  };

  useEffect(() => {
    if (!removingAddonId) return;
    const allRows = [
      ...credits,
      ...additionalFeatures,
      ...organisationAddonRows,
    ];
    const row = allRows.find((r) => r.addonId === removingAddonId);
    if (!row) {
      setRemovingAddonId(null);
      return;
    }
    const isScheduledForRemoval =
      row.usage?.scheduled &&
      row.usage.scheduled.overages === 0 &&
      Number(row.usage.scheduled.overagesChange) < 0;
    if (isScheduledForRemoval) {
      setRemovingAddonId(null);
    }
  }, [removingAddonId, credits, additionalFeatures, organisationAddonRows]);

  const { execute: executeCancelScheduled, loading: isLoadingCancelScheduled } =
    usePush(SubscriptionService.cancelScheduledChange, {
      errorToast: true,
      successToast: "Scheduled change cancelled",
    });

  const onCancelScheduledChange = async (row) => {
    if (!row?.addon || !row?.usage?.scheduled) {
      return;
    }

    const isUsersAddon =
      row.addon.id === addonIds.USER ||
      row.addon.id === addonIds.ADDITIONAL_USERS;
    const moduleNames = isUsersAddon
      ? [Modules.ORGANISATION, Modules.RECEIVABLES]
      : row?.moduleName
        ? [row.moduleName]
        : [];

    await executeCancelScheduled({
      shortCode,
      addonId: row.addon.id,
      moduleName: row?.moduleName,
      moduleNames,
    });
    refetchOrganisationAddons();
    refetchSubscriptionData();
  };

  const activeModuleCount = activeModuleSubscriptions.filter(
    (module) => module?.isActive && module?.id !== ORGANISATION_MODULE_ID,
  ).length;

  const receivablesVisiblePlans = useMemo(() => {
    if (!Array.isArray(plans) || !plans.length) {
      if (!selectedPlan) return [];

      return [
        {
          name: selectedPlan.name || "current-plan",
          title: selectedPlan.title || selectedPlan.name || "Current plan",
          amount: currentPlanAmount,
          formattedAmount: formatCurrency(currentPlanAmount, currencyCode),
          isCurrent: true,
          canUpgrade: false,
          maxRevenueText: null,
          features: selectedPlan?.features,
          freeUsers: resolveFreeUsers(
            selectedPlan?.features,
            selectedPlan?.addons?.users?.planLimit,
          ),
          includedDefaults: [],
          bulletTexts: [],
        },
      ];
    }

    return plans
      .filter((plan) => !isTrialPlanName(plan?.name))
      .map((plan) => {
        const periodPrice = plan.pricesByPeriod?.[comparisonPeriod];
        const amount = Number(periodPrice?.amount || 0);
        const formattedAmount =
          periodPrice?.formattedAmount || formatCurrency(amount, currencyCode);
        const isCurrentCadence =
          comparisonPeriod === normalizeOfferPeriod(selectedPlanPeriod);
        const isCurrent = plan.name === selectedPlan?.name && isCurrentCadence;
        const canUpgrade = !isCurrent && amount > currentPlanAmount;

        const maxRevenueText = getPlanMaxRevenueText(
          plan,
          currencyCode,
          formatCurrency,
        );

        const bulletTexts = (plan.bullets || [])
          .filter((bullet) => bullet.enabled)
          .slice(0, 6)
          .map((bullet) => ({
            id: bullet.id,
            label:
              typeof bullet.label === "string"
                ? bullet.label
                : String(bullet.id),
          }));

        const includedInPlanLimits = mapIncludedInPlanToPlanLimit(
          plan.addons,
          plan.features,
        );

        const includedDefaults = Object.entries(includedInPlanLimits)
          .map(([addonKey, addonData]) =>
            getIncludedDefaultLabel(addonKey, addonData),
          )
          .filter(Boolean);

        const resolvedIncludedDefaults = includedDefaults.length
          ? includedDefaults
          : getFeatureLimitDefaults(plan.features);

        return {
          name: plan.name,
          title: plan.title,
          amount,
          formattedAmount,
          isCurrent,
          canUpgrade,
          maxRevenueText,
          features: plan.features,
          freeUsers: resolveFreeUsers(
            plan.features,
            plan.addons?.users?.planLimit,
          ),
          includedDefaults: resolvedIncludedDefaults,
          bulletTexts,
        };
      })
      .sort((a, b) => a.amount - b.amount);
  }, [
    plans,
    comparisonPeriod,
    formatCurrency,
    currencyCode,
    selectedPlan,
    selectedPlanPeriod,
    currentPlanAmount,
  ]);

  const selectedPlanRevenueText =
    receivablesVisiblePlans.find((plan) => plan.name === selectedPlan?.name)
      ?.maxRevenueText ||
    getPlanMaxRevenueText(selectedPlan, currencyCode, formatCurrency);

  const compareCurrentPlanAmount =
    selectedModule?.id !== BASE_MODULE_ID
      ? selectedModuleAmountMinor / 100
      : currentPlanAmount;

  const selectedModuleVisiblePlans = useMemo(() => {
    if (!resolvedModulePlansById || selectedModule?.id === BASE_MODULE_ID)
      return [];

    const periodKey =
      comparisonPeriod === BillingPeriods.YEARLY
        ? BillingPeriods.YEARLY
        : BillingPeriods.MONTHLY;
    const normalizedSelectedPlanName = normalizeCompareToken(
      selectedModule?.planName,
    );

    return Object.entries(resolvedModulePlansById)
      .map(([planKey, plan]) => {
        if (!plan) return null;

        const planIdentifier = plan.name || planKey;
        if (isTrialPlanName(planIdentifier)) return null;

        const selectedPrice = (plan.prices || []).find(
          (price) =>
            String(price?.currency || "").toUpperCase() ===
              String(currencyCode || "").toUpperCase() &&
            price?.period === periodKey,
        );

        if (!selectedPrice) return null;

        const amount = Number(selectedPrice.amount || 0) / 100;
        const isCurrentCadence =
          comparisonPeriod === normalizeOfferPeriod(selectedPlanPeriod);
        const isCurrent =
          normalizeCompareToken(planIdentifier) ===
            normalizedSelectedPlanName ||
          normalizeCompareToken(plan.title) === normalizedSelectedPlanName;
        const isCurrentPlan = isCurrent && isCurrentCadence;

        return {
          name: planIdentifier,
          title: plan.title || toDisplayLabel(planIdentifier, "Plan"),
          amount,
          formattedAmount: formatCurrency(amount, currencyCode),
          isCurrent: isCurrentPlan,
          canUpgrade: !isCurrentPlan && amount > compareCurrentPlanAmount,
          maxRevenueText: null,
          features: plan.features,
          freeUsers: resolveFreeUsers(
            plan.features,
            plan.addons?.users?.planLimit,
          ),
          includedDefaults: getFeatureLimitDefaults(plan.features || {}),
          metadataFeatureFlags: getMetadataFeatureFlags(
            plan.metadataFeatures || {},
            plan.features || {},
          ),
          bulletTexts: [],
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.amount - b.amount);
  }, [
    resolvedModulePlansById,
    selectedModule?.id,
    selectedModule?.planName,
    comparisonPeriod,
    selectedPlanPeriod,
    currencyCode,
    formatCurrency,
    compareCurrentPlanAmount,
  ]);
  const compareVisiblePlans =
    selectedModule?.id === BASE_MODULE_ID
      ? receivablesVisiblePlans
      : selectedModuleVisiblePlans;

  return {
    activeAddonId,
    setActiveAddonId,
    showComparePlans,
    setShowComparePlans,
    setTargetPlanName,
    loading,
    isInitialLoading,
    isModuleSwitching,
    selectedModule: selectedModule,
    moduleSubscriptions: activeModuleSubscriptions,
    availableModules: availableModuleOptions,
    selectedModuleSupportsAddons,
    selectedModuleAmountMinor,
    planAmountMinor,
    moduleTotalCurrentCadenceMinor,
    moduleTotalAnnualEquivalentMinor,
    activeModuleCount,
    selectedPlanRevenueText,
    visiblePlans: compareVisiblePlans,
    compareCurrentPlanAmount,
    selectedOfferPeriod,
    setSelectedOfferPeriod,
    isSelectedModuleOfferFlow,
    isSelectedModuleInTrial: Boolean(selectedModule?.isInTrial),
    isSelectedModuleExpiredTrial: isExpiredTrialModule(selectedModule),
    selectedModuleTrialEndsAt: selectedModule?.trialEndsAt || null,
    isStartingModuleTrial,
    startSelectedModuleTrial,
    credits,
    additionalFeatures,
    organisationAddonRows,
    refetchOrganisationAddons,
    refetchSelectedModuleAddons,
    onActionClick,
    onRemoveClick,
    removingAddonId,
    onCancelScheduledChange,
    isLoadingCancelScheduled,
    selectModuleById: setSelectedModuleId,
  };
};

export default useAddonsState;
